import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Search, MapPin, Briefcase, ChevronRight, Star, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_jobs')
        .select('*, hr_branches(name)')
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          job.department.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = locationFilter ? job.location.includes(locationFilter) : true;
    return matchesSearch && matchesLocation;
  });

  return (
    <div className="bg-[#f5f5f7] min-h-screen text-[#1d1d1f] pt-24 pb-12 font-sans">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight">Find your perfect role.</h1>
          <div className="max-w-3xl mx-auto relative">
            <div className="relative flex items-center">
              <Search className="absolute left-6 text-gray-400 w-6 h-6" />
              <input
                type="text"
                placeholder="Search by role or keyword"
                className="w-full pl-16 pr-6 py-5 rounded-full border border-gray-300 text-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#065DC0] focus:border-transparent transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ borderRadius: '9999px' }}
              />
            </div>
            <p className="text-gray-500 mt-4 text-sm">Most corporate roles require English-language skills.</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-1/4">
            <div className="sticky top-24">
              <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-semibold">Refine by</h2>
                <button 
                  onClick={() => { setSearchQuery(''); setLocationFilter(''); }}
                  className="text-[#065DC0] text-sm font-medium hover:underline"
                >
                  Clear all
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-3 flex justify-between items-center cursor-pointer">
                    Location
                    <span className="text-gray-400 font-normal">+</span>
                  </h3>
                  <div className="space-y-2">
                    {/* Unique Locations */}
                    {Array.from(new Set(jobs.map(j => j.location))).map(loc => (
                      <label key={loc} className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="radio" 
                          name="location" 
                          checked={locationFilter === loc}
                          onChange={() => setLocationFilter(loc)}
                          className="w-4 h-4 text-[#065DC0] focus:ring-[#065DC0]" 
                        />
                        <span className="text-sm">{loc}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="font-semibold mb-3 flex justify-between items-center cursor-pointer text-gray-600">
                    Department
                    <span className="text-gray-400 font-normal">+</span>
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* Results List */}
          <div className="w-full lg:w-3/4">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">{filteredJobs.length} Result(s)</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Sort by:</span>
                <select className="bg-transparent font-medium focus:outline-none cursor-pointer">
                  <option>Newest</option>
                  <option>Most Relevant</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-4 border-[#065DC0] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredJobs.map((job, index) => (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    key={job.id} 
                    className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-2xl font-semibold text-[#065DC0] group-hover:underline flex items-center gap-1">
                        {job.title}
                        <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <button className="text-gray-400 hover:text-[#065DC0] transition-colors" onClick={(e) => { e.stopPropagation(); }}>
                        <Star className="w-6 h-6" />
                      </button>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-[#1d1d1f] mb-4 font-medium">
                      <span>{job.department}</span>
                      <span className="hidden sm:inline text-gray-300">|</span>
                      <span>{job.location}</span>
                    </div>

                    <p className="text-gray-500 text-sm line-clamp-2 mb-4">
                      {job.description}
                    </p>

                    <div className="text-sm text-gray-400 font-medium pt-4 border-t border-gray-100">
                      Posted: {new Date(job.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </motion.div>
                ))}

                {filteredJobs.length === 0 && !loading && (
                  <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                    <p className="text-gray-500 text-lg">No roles found matching your criteria.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
